<template>
  <div
    style="width: 100%"
    class="no-page-break-inside"
    v-observe-visibility="{
      callback: visibilityChanged,
      once: true,
      throttle: 100,
      intersection: {
        rootMargin: '200px 0px'
      }
    }"
  >

    <div v-if="lazy_load && !isVisible" class="chart-placeholder border border-light rounded mt-2 d-flex align-items-center justify-content-center"
         :style="'height: ' + (chartData?.options?.chart_height || 400) + 'px;'">
      <b-spinner variant="secondary" label="Loading chart..."></b-spinner>
    </div>
    <canvas
      v-show="(isVisible || !lazy_load) && isRendered"
      :id="`chart_canvas_${chart_id}`"
      :style="'max-height: ' + (chartData?.options?.chart_height || 400) + 'px;'"
      ref="qesgChart"
      class="border border-light rounded mt-2"
    ></canvas>
    <p v-if="chartData && chartData.options" class="fw-400 f-90 mb-0 text-muted"  style="white-space: pre-line">
      <span v-if="lang === 'KOR'">
        {{ chartData?.options?.footnote }}
      </span>
      <span v-else>
        {{ chartData_e?.options?.footnote }}
      </span>
    </p>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'ChartCanvasSb',
  props: {
    chart_id: { type: Number, default: () => null },
    chartData: { type: Object, default: null },
    chartData_e: { type: Object, default: null },
    lazy_load: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false },
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('charts', ['chart_data', 'edited_chart_id']),
    main_color() {
      return `color:` + this.selected_report.main_color;
    },
  },
  data() {
    return {
      chart: null,
      isVisible: false,
      isRendered: false,
      renderTimeout: null,
    };
  },
  watch: {
    lang: {
      handler() {
        if (this.isVisible || !this.lazy_load) {
          // isRendered 플래그를 false로 설정하여 재렌더링 강제
          this.isRendered = false;
          this.debounceRenderChart();
        }
      },
    },
  },
  mounted() {
    // If lazy_load is disabled, show immediately
    if (this.isVisible || !this.lazy_load) {
      this.isVisible = true;
      this.$nextTick(() => {
        this.debounceRenderChart();
      });
    }
  },
  beforeDestroy() {
    this.cleanupChart();
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }
  },
  methods: {
    formatUnit(context) {
      const unitLabel = this.lang === 'KOR' ? '단위' : 'unit';
      return context.parsed.y !== undefined
        ? `${context.dataset.label}: ${context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`
        : `${context.dataset.label}: ${context.parsed.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`;
    },

    visibilityChanged(isVisible) {
      if (isVisible && !this.isVisible) {
        this.isVisible = true;
        this.$nextTick(() => {
          this.debounceRenderChart();
        });
      }
    },

    debounceRenderChart() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }
      this.renderTimeout = setTimeout(() => {
        this.renderChart();
      }, 100);
    },

    cleanupChart() {
      if (this.chart) {
        try {
          this.chart.destroy();
        } catch (e) {
          console.warn('Chart destroy error:', e);
        }
        this.chart = null;
      }

      // Canvas 컨텍스트 명시적 정리 (Safari 메모리 최적화)
      if (this.$refs.qesgChart) {
        const canvas = this.$refs.qesgChart;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Safari에서 메모리 해제 강제
          canvas.width = 0;
          canvas.height = 0;
        }
      }
    },

    async renderChart() {
      if ((!this.isVisible && this.lazy_load) || !this.$refs.qesgChart) {
        return;
      }
      
      // 기존 차트 완전 정리
      this.cleanupChart();
      
      // 렌더링 전 메모리 정리를 위한 대기
      await new Promise(resolve => setTimeout(resolve, 50));

      try {
        let chartOptions = this.lang !== 'ENG' ? { ...this.chartData } : { ...this.chartData_e };

        if (!chartOptions || !chartOptions?.options) {
          console.warn("Chart options not available for language:", this.lang);
          return;
        }

        // Safari 메모리 최적화를 위한 설정
        chartOptions.options = {
          ...chartOptions.options,
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0 // Safari에서 애니메이션 비활성화로 메모리 절약
          },
          interaction: {
            intersect: false,
            mode: 'point'
          }
        };

        chartOptions.plugins = [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            },
          },
        ];

        chartOptions.options.plugins = {
          ...chartOptions.options.plugins,
          tooltip: {
            intersect: true,
            callbacks: {
              label: this.formatUnit,
            },
          }
        };

        // Canvas 크기 제한 (Safari 메모리 최적화)
        const canvas = this.$refs.qesgChart;
        const maxWidth = Math.min(canvas.parentElement.clientWidth || 800, 1200);
        const maxHeight = Math.min(chartOptions.options.chart_height || 400, 600);

        canvas.style.maxWidth = maxWidth + 'px';
        canvas.style.maxHeight = maxHeight + 'px';

        const ctx = canvas.getContext('2d');
        this.chart = new Chart(ctx, chartOptions);
        this.isRendered = true;

      } catch (error) {
        console.error('Chart render error:', error);
        this.isRendered = false;
      }
    },
  },
};
</script>

<style scoped>
.chart-placeholder {
  background-color: #f8f9fa;
}

.chart-placeholder:hover {
  background-color: #e9ecef;
}

/* Safari 메모리 최적화를 위한 Canvas 스타일 */
canvas {
  image-rendering: auto;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}
</style>
